export const YOUTUBE_MAX_VIDEO_DURATION_SECONDS = 90;

export type YouTubePlayer = {
  getDuration: () => number;
  getCurrentTime: () => number;
  destroy: () => void;
};

type YouTubePlayerEvent = {
  target: YouTubePlayer;
};

// YT.PlayerState. Only PLAYING means the video is actually running — BUFFERING and CUED
// look active but are not playback, which matters when playback time is what gates a
// reported view.
export const YOUTUBE_PLAYER_STATE = {
  UNSTARTED: -1,
  ENDED: 0,
  PLAYING: 1,
  PAUSED: 2,
  BUFFERING: 3,
  CUED: 5,
} as const;

export type YouTubeStateChangeEvent = {
  target: YouTubePlayer;
  data: number;
};

type YouTubePlayerConstructor = new (
  element: HTMLElement,
  options: {
    videoId: string;
    playerVars?: Record<string, string | number>;
    events: {
      onReady: (event: YouTubePlayerEvent) => void;
      onError: () => void;
      onStateChange?: (event: YouTubeStateChangeEvent) => void;
    };
  },
) => YouTubePlayer;

declare global {
  interface Window {
    YT?: {
      Player: YouTubePlayerConstructor;
    };
    onYouTubeIframeAPIReady?: () => void;
  }
}

export const parseYouTubeId = (url: string): string | null => {
  const trimmed = url.trim();
  if (/^[a-zA-Z0-9_-]{11}$/.test(trimmed)) return trimmed;

  const match = trimmed.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);

  return match?.[1] ?? null;
};

// Exported so the focused loop can mount a real YT.Player: a plain `/embed/` iframe emits
// no playback events, which makes it impossible to tell a played video from one that was
// merely on screen.
export const loadYouTubeIframeApi = () =>
  new Promise<void>((resolve, reject) => {
    if (window.YT?.Player) {
      resolve();
      return;
    }

    const existingScript = document.querySelector<HTMLScriptElement>('script[src="https://www.youtube.com/iframe_api"]');
    const previousReadyHandler = window.onYouTubeIframeAPIReady;

    window.onYouTubeIframeAPIReady = () => {
      previousReadyHandler?.();
      resolve();
    };

    if (existingScript) {
      existingScript.addEventListener('error', () => reject(new Error('Could not load YouTube player.')), {
        once: true,
      });
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://www.youtube.com/iframe_api';
    script.async = true;
    script.onerror = () => reject(new Error('Could not load YouTube player.'));
    document.body.appendChild(script);
  });

const getYouTubeDurationSeconds = async (videoId: string) => {
  await loadYouTubeIframeApi();

  return new Promise<number>((resolve, reject) => {
    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.left = '-9999px';
    container.style.top = '0';
    container.style.width = '1px';
    container.style.height = '1px';
    container.style.opacity = '0';
    container.style.pointerEvents = 'none';
    document.body.appendChild(container);

    const cleanup = (player?: YouTubePlayer) => {
      player?.destroy();
      container.remove();
    };

    const player = new window.YT!.Player(container, {
      videoId,
      events: {
        onReady: (event) => {
          const duration = event.target.getDuration();
          cleanup(event.target);
          resolve(duration);
        },
        onError: () => {
          cleanup(player);
          reject(new Error('Could not read YouTube video duration.'));
        },
      },
    });
  });
};

export const getYouTubeDurationValidationError = async (url: string): Promise<string | null> => {
  const videoId = parseYouTubeId(url);

  if (!videoId) return 'Add a valid YouTube link.';

  const durationSeconds = await getYouTubeDurationSeconds(videoId);

  return durationSeconds > YOUTUBE_MAX_VIDEO_DURATION_SECONDS
    ? `YouTube video must be ${YOUTUBE_MAX_VIDEO_DURATION_SECONDS} seconds or less.`
    : null;
};
